<template>
  <!-- Begin page -->
  <div id="layout-wrapper">
    <NavigationBar/>
    <!-- Start right Content here -->
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Add New Employee</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)">Home</a></li>
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('employee-register')">Employees</a></li>
                    <li class="breadcrumb-item active">New Employee</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Form -->
          <div class="row justify-content-center">
            <div class="col-lg-10">
              <!-- Main Card -->
              <div class="card">
                <div class="card-header text-white">
                  <h5 class="mb-0">
                    <i class="mdi mdi-account-plus me-2"></i>
                    Employee Registration Form
                  </h5>
                </div>

                <div class="card-body">
                  <!-- Mandatory Fields Section -->
                  <div class="mandatory-section">
                    <!-- Personal Information -->
                    <div class="row mb-4">
                      <div class="col-12">
                        <div class="bg-light p-3 mb-3 rounded">
                          <h6 class="mb-0 text-primary">
                            <i class="mdi mdi-account-circle me-2"></i>
                            Personal Information (Required)
                          </h6>
                        </div>
                      </div>

                      <!-- Employee Number -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Employee Number <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-identifier"></i></span>
                          <input
                              v-model="employee.empNo"
                              type="text"
                              class="form-control"
                              :class="{'is-invalid': errors.empNo}"
                              placeholder="e.g., EMP001"
                              @blur="validateField('empNo')"
                          >
                        </div>
                        <small v-if="errors.empNo" class="text-danger">{{ errors.empNo }}</small>
                      </div>

                      <!-- First Name -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          First Name <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-account"></i></span>
                          <input
                              v-model="employee.firstName"
                              type="text"
                              class="form-control"
                              :class="{'is-invalid': errors.firstName}"
                              placeholder="Enter first name"
                              @blur="validateField('firstName')"
                          >
                        </div>
                        <small v-if="errors.firstName" class="text-danger">{{ errors.firstName }}</small>
                      </div>

                      <!-- Middle Name -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Middle Name
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-account"></i></span>
                          <input
                              v-model="employee.middleName"
                              type="text"
                              class="form-control"
                              placeholder="Enter middle name (optional)"
                          >
                        </div>
                      </div>

                      <!-- Last Name -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Last Name <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-account"></i></span>
                          <input
                              v-model="employee.lastName"
                              type="text"
                              class="form-control"
                              :class="{'is-invalid': errors.lastName}"
                              placeholder="Enter last name"
                              @blur="validateField('lastName')"
                          >
                        </div>
                        <small v-if="errors.lastName" class="text-danger">{{ errors.lastName }}</small>
                      </div>

                      <!-- Email -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Work Email <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-email"></i></span>
                          <input
                              v-model="employee.email"
                              type="email"
                              class="form-control"
                              :class="{'is-invalid': errors.email}"
                              placeholder="email@company.com"
                              @blur="validateField('email')"
                          >
                        </div>
                        <small v-if="errors.email" class="text-danger">{{ errors.email }}</small>
                      </div>

                      <!-- Personal Email (mandatory for Contract/Consultant/Intern) -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Personal Email
                          <span v-if="requiresPersonalEmail" class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-email-outline"></i></span>
                          <input
                              v-model="employee.personalEmail"
                              type="email"
                              class="form-control"
                              :class="{'is-invalid': errors.personalEmail}"
                              placeholder="personal@gmail.com"
                              @blur="validateField('personalEmail')"
                          >
                        </div>
                        <small v-if="errors.personalEmail" class="text-danger">{{ errors.personalEmail }}</small>
                        <small v-else-if="requiresPersonalEmail" class="text-muted">Required for {{ employee.employmentType }}</small>
                      </div>

                      <!-- Phone -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Phone Number <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-phone"></i></span>
                          <input
                              v-model="employee.phone"
                              type="tel"
                              class="form-control"
                              :class="{'is-invalid': errors.phone}"
                              placeholder="07XXXXXXXX"
                              @blur="validateField('phone')"
                          >
                        </div>
                        <small v-if="errors.phone" class="text-danger">{{ errors.phone }}</small>
                      </div>

                      <!-- National ID -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          National ID <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-card-account-details"></i></span>
                          <input
                              v-model="employee.nationalId"
                              type="text"
                              class="form-control"
                              :class="{'is-invalid': errors.nationalId}"
                              placeholder="Enter ID number"
                              @blur="validateField('nationalId')"
                          >
                        </div>
                        <small v-if="errors.nationalId" class="text-danger">{{ errors.nationalId }}</small>
                      </div>
                    </div>

                    <!-- Employment Information -->
                    <div class="row mb-4">
                      <div class="col-12">
                        <div class="bg-light p-3 mb-3 rounded">
                          <h6 class="mb-0 text-primary">
                            <i class="mdi mdi-briefcase me-2"></i>
                            Employment Information (Required)
                          </h6>
                        </div>
                      </div>

                      <!-- Department -->
                      <div class="col-md-6 mb-3">
                        <label class="form-label">
                          Department <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-office-building"></i></span>
                          <select
                              v-model="employee.departmentID"
                              class="form-select"
                              :class="{'is-invalid': errors.departmentID}"
                              @change="validateField('departmentID')"
                          >
                            <option value="">Select Department</option>
                            <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                              {{ dept.departmentName }}
                            </option>
                          </select>
                        </div>
                        <small v-if="errors.departmentID" class="text-danger">{{ errors.departmentID }}</small>
                      </div>

                      <!-- Employment Type -->
                      <div class="col-md-6 mb-3">
                        <label class="form-label">
                          Employment Type <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-badge-account"></i></span>
                          <select
                              v-model="employee.employmentType"
                              class="form-select"
                              :class="{'is-invalid': errors.employmentType}"
                              @change="validateField('employmentType')"
                          >
                            <option value="">Select Type</option>
                            <option value="Permanent">Permanent</option>
                            <option value="Contract">Contract</option>
                            <option value="Temporary">Temporary</option>
                            <option value="Intern">Intern</option>
                            <option value="Consultant">Consultant</option>
                            <option value="Expatriate">Expatriate</option>
                          </select>
                        </div>
                        <small v-if="errors.employmentType" class="text-danger">{{ errors.employmentType }}</small>
                      </div>

                      <!-- Position -->
                      <div class="col-md-6 mb-3">
                        <label class="form-label">
                          Position <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-briefcase"></i></span>
                          <select
                              v-model="employee.positionId"
                              class="form-select"
                              :class="{'is-invalid': errors.positionTitle}"
                              @change="onPositionChange"
                          >
                            <option value="">Select Position</option>
                            <option v-for="pos in positionOptions" :key="pos.id" :value="pos.id">
                              {{ pos.positionName }}
                            </option>
                          </select>
                        </div>
                        <small v-if="errors.positionTitle" class="text-danger">{{ errors.positionTitle }}</small>
                      </div>

                      <!-- Gender -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Gender <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-gender-male-female"></i></span>
                          <select
                              v-model="employee.gender"
                              class="form-select"
                              :class="{'is-invalid': errors.gender}"
                              @change="validateField('gender')"
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <small v-if="errors.gender" class="text-danger">{{ errors.gender }}</small>
                      </div>

                      <!-- Date of Birth -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Date of Birth <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-calendar"></i></span>
                          <input
                              v-model="employee.dateOfBirth"
                              type="date"
                              class="form-control"
                              :class="{'is-invalid': errors.dateOfBirth}"
                              :max="maxDate"
                              @blur="validateField('dateOfBirth')"
                          >
                        </div>
                        <small v-if="errors.dateOfBirth" class="text-danger">{{ errors.dateOfBirth }}</small>
                      </div>

                      <!-- Nationality -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Nationality <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-flag"></i></span>
                          <select
                              v-model="employee.nationality"
                              class="form-select"
                              :class="{'is-invalid': errors.nationality}"
                              @change="validateField('nationality')"
                          >
                            <option value="">Select Country</option>
                            <option value="Kenya">Kenya</option>
                            <option value="Uganda">Uganda</option>
                            <option value="Tanzania">Tanzania</option>
                            <option value="Rwanda">Rwanda</option>
                            <option value="Burundi">Burundi</option>
                            <option value="Ethiopia">Ethiopia</option>
                            <option value="Somalia">Somalia</option>
                            <option value="South Sudan">South Sudan</option>
                            <option value="Sudan">Sudan</option>
                            <option value="Democratic Republic of Congo">Democratic Republic of Congo</option>
                            <option value="South Africa">South Africa</option>
                            <option value="Nigeria">Nigeria</option>
                            <option value="Ghana">Ghana</option>
                            <option value="Egypt">Egypt</option>
                            <option value="Morocco">Morocco</option>
                            <option value="Algeria">Algeria</option>
                            <option value="Tunisia">Tunisia</option>
                            <option value="Libya">Libya</option>
                            <option value="Cameroon">Cameroon</option>
                            <option value="Zimbabwe">Zimbabwe</option>
                            <option value="Botswana">Botswana</option>
                            <option value="Namibia">Namibia</option>
                            <option value="Zambia">Zambia</option>
                            <option value="Malawi">Malawi</option>
                            <option value="Mozambique">Mozambique</option>
                            <option value="Angola">Angola</option>
                            <option value="United States">United States</option>
                            <option value="United Kingdom">United Kingdom</option>
                            <option value="Canada">Canada</option>
                            <option value="Australia">Australia</option>
                            <option value="India">India</option>
                            <option value="Pakistan">Pakistan</option>
                            <option value="Bangladesh">Bangladesh</option>
                            <option value="China">China</option>
                            <option value="Japan">Japan</option>
                            <option value="South Korea">South Korea</option>
                            <option value="Philippines">Philippines</option>
                            <option value="Indonesia">Indonesia</option>
                            <option value="Malaysia">Malaysia</option>
                            <option value="Singapore">Singapore</option>
                            <option value="Thailand">Thailand</option>
                            <option value="Vietnam">Vietnam</option>
                            <option value="Germany">Germany</option>
                            <option value="France">France</option>
                            <option value="Italy">Italy</option>
                            <option value="Spain">Spain</option>
                            <option value="Netherlands">Netherlands</option>
                            <option value="Belgium">Belgium</option>
                            <option value="Switzerland">Switzerland</option>
                            <option value="Austria">Austria</option>
                            <option value="Sweden">Sweden</option>
                            <option value="Norway">Norway</option>
                            <option value="Denmark">Denmark</option>
                            <option value="Finland">Finland</option>
                            <option value="Poland">Poland</option>
                            <option value="Russia">Russia</option>
                            <option value="Ukraine">Ukraine</option>
                            <option value="Brazil">Brazil</option>
                            <option value="Mexico">Mexico</option>
                            <option value="Argentina">Argentina</option>
                            <option value="Chile">Chile</option>
                            <option value="Colombia">Colombia</option>
                            <option value="Peru">Peru</option>
                            <option value="Venezuela">Venezuela</option>
                            <option value="Saudi Arabia">Saudi Arabia</option>
                            <option value="United Arab Emirates">United Arab Emirates</option>
                            <option value="Qatar">Qatar</option>
                            <option value="Kuwait">Kuwait</option>
                            <option value="Israel">Israel</option>
                            <option value="Turkey">Turkey</option>
                            <option value="Iran">Iran</option>
                            <option value="Iraq">Iraq</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <small v-if="errors.nationality" class="text-danger">{{ errors.nationality }}</small>
                      </div>

                      <!-- Hire Date -->
                      <div class="col-md-6 col-lg-4 mb-3">
                        <label class="form-label">
                          Hire Date <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-calendar-check"></i></span>
                          <input
                              v-model="employee.hireDate"
                              type="date"
                              class="form-control"
                              :class="{'is-invalid': errors.hireDate}"
                              @blur="validateField('hireDate')"
                          >
                        </div>
                        <small v-if="errors.hireDate" class="text-danger">{{ errors.hireDate }}</small>
                      </div>
                    </div>

                    <!-- Remove Optional Fields Section -->
                    <!-- All fields are now mandatory -->
                  </div>

                  <!-- Action Buttons -->
                  <div class="row mt-4">
                    <div class="col-12">
                      <hr>
                      <div class="d-flex justify-content-between">
                        <button
                            type="button"
                            @click="resetForm"
                            class="btn btn-outline-secondary"
                        >
                          <i class="mdi mdi-refresh me-2"></i>
                          Reset Form
                        </button>
                        <div>
                          <button
                              type="button"
                              @click="validateAndSubmit"
                              class="btn btn-primary"
                              :disabled="loading"
                          >
                            <span v-if="!loading">
                              <i class="mdi mdi-check me-2"></i>
                              Create Employee
                            </span>
                            <span v-else>
                              <span class="spinner-border spinner-border-sm me-2"></span>
                              Creating...
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal (Vue-controlled, no Bootstrap) -->
    <div v-if="showSuccessModal" class="modal-overlay" @click.self="showSuccessModal = false">
      <div class="modal-dialog-custom">
        <div class="modal-content-custom">
          <div class="modal-body-custom text-center p-5">
            <div class="mb-4">
              <div class="avatar-lg mx-auto">
                <div class="avatar-title bg-light text-success rounded-circle">
                  <i class="bx bx-check-circle font-size-48"></i>
                </div>
              </div>
            </div>
            <h4 class="mb-3">Employee Created Successfully!</h4>
            <p class="text-muted mb-4">{{ successMessage }}</p>
            <div class="d-grid gap-2">
              <button type="button" @click="showSuccessModal = false; gotTo('employee-register')" class="btn btn-primary">
                <i class="bx bx-list-ul me-2"></i> View All Employees
              </button>
              <button type="button" @click="createAnotherAndCloseModal" class="btn btn-outline-primary">
                <i class="bx bx-plus me-2"></i> Add Another Employee
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Modal (Vue-controlled, no Bootstrap) -->
    <div v-if="showErrorModal" class="modal-overlay" @click.self="showErrorModal = false">
      <div class="modal-dialog-custom">
        <div class="modal-content-custom">
          <div class="modal-body-custom text-center p-5">
            <div class="mb-4">
              <div class="avatar-lg mx-auto">
                <div class="avatar-title bg-light text-danger rounded-circle">
                  <i class="bx bx-error-circle font-size-48"></i>
                </div>
              </div>
            </div>
            <h4 class="mb-3">Error Creating Employee</h4>
            <p class="text-muted mb-4">{{ errorMessage }}</p>
            <button type="button" @click="showErrorModal = false" class="btn btn-danger">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeCreateSimplified",
  components: { NavigationBar },
  data() {
    return {
      // Form Data
      employee: {
        empNo: '',
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        personalEmail: '',
        phone: '',
        nationalId: '',
        gender: '',
        dateOfBirth: '',
        nationality: 'Kenya',
        employmentType: '',
        positionTitle: '',
        positionId: '',
        position_id: null,
        hireDate: '',
        departmentID: '',
        departmentName: ''
      },

      // UI State
      loading: false,
      departments: [],
      positions: [],

      // Validation
      errors: {},

      // Messages
      successMessage: '',
      errorMessage: '',
      showSuccessModal: false,
      showErrorModal: false,

      // Computed max date for DOB (18 years ago)
      maxDate: ''
    };
  },

  computed: {
    requiresPersonalEmail() {
      return ['Contract', 'Consultant', 'Intern'].includes(this.employee.employmentType || '');
    },
    positionOptions() {
      if (!this.positions || !this.positions.length) return [];
      return this.positions.map(p => {
        if (typeof p === 'object' && p !== null && 'id' in p && 'positionName' in p) {
          return { id: p.id, positionName: p.positionName };
        }
        const name = String(p);
        return { id: name, positionName: name };
      });
    }
  },

  mounted() {
    this.loadDepartments();
    this.loadPositions();
    this.setMaxDate();
  },

  methods: {
    setMaxDate() {
      const today = new Date();
      const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
      this.maxDate = eighteenYearsAgo.toISOString().split('T')[0];
    },

    async loadDepartments() {
      try {
        const data = new FormData();
        data.append("function", "getDepartments");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.departments = res.data.data?.departments || res.data.departments || [];
        } else {
          console.warn("Failed to load departments");
          this.departments = [];
        }
      } catch (error) {
        console.error("Error loading departments:", error);
        this.departments = [];
      }
    },

    async loadPositions() {
      try {
        const data = new FormData();
        data.append("function", "getPositions");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.positions = res.data.data?.positions || res.data.positions || [];
        } else {
          console.warn("Failed to load positions");
          // Use default positions if API fails
          this.positions = [
            'Manager - Operations',
            'Manager - Human Resources',
            'Manager - Finance',
            'Manager - Sales',
            'Head of Department',
            'Team Lead',
            'Staff Officer',
            'Coordinator',
            'Analyst',
            'Specialist',
            'Consultant',
            'Executive',
            'Associate',
            'Supervisor',
            'Director'
          ];
        }
      } catch (error) {
        console.error("Error loading positions:", error);
        // Use default positions if API fails
        this.positions = [
          'Manager - Operations',
          'Manager - Human Resources',
          'Manager - Finance',
          'Manager - Sales',
          'Head of Department',
          'Team Lead',
          'Staff Officer',
          'Coordinator',
          'Analyst',
          'Specialist',
          'Consultant',
          'Executive',
          'Associate',
          'Supervisor',
          'Director'
        ];
      }
    },

    validateField(field) {
      // Clear previous error for this field
      delete this.errors[field];

      // Employee Number validation
      if (field === 'empNo' && !this.employee.empNo) {
        this.errors[field] = 'Employee number is required';
      }

      // Name validations
      if (field === 'firstName' && !this.employee.firstName) {
        this.errors[field] = 'First name is required';
      }

      if (field === 'lastName' && !this.employee.lastName) {
        this.errors[field] = 'Last name is required';
      }

      // Email validation
      if (field === 'email') {
        if (!this.employee.email) {
          this.errors[field] = 'Email is required';
        } else if (!this.isValidEmail(this.employee.email)) {
          this.errors[field] = 'Please enter a valid email address';
        }
      }

      // Phone validation
      if (field === 'phone') {
        if (!this.employee.phone) {
          this.errors[field] = 'Phone number is required';
        } else if (!this.isValidPhone(this.employee.phone)) {
          this.errors[field] = 'Please enter a valid phone number';
        }
      }

      // National ID validation
      if (field === 'nationalId' && !this.employee.nationalId) {
        this.errors[field] = 'National ID is required';
      }

      // Gender validation
      if (field === 'gender' && !this.employee.gender) {
        this.errors[field] = 'Gender is required';
      }

      // Date of Birth validation
      if (field === 'dateOfBirth' && !this.employee.dateOfBirth) {
        this.errors[field] = 'Date of birth is required';
      }

      // Nationality validation
      if (field === 'nationality' && !this.employee.nationality) {
        this.errors[field] = 'Nationality is required';
      }

      // Hire Date validation
      if (field === 'hireDate' && !this.employee.hireDate) {
        this.errors[field] = 'Hire date is required';
      }

      // Department validation
      if (field === 'departmentID' && !this.employee.departmentID) {
        this.errors[field] = 'Department is required';
      }

      // Employment Type validation
      if (field === 'employmentType' && !this.employee.employmentType) {
        this.errors[field] = 'Employment type is required';
      }

      // Position validation
      if (field === 'positionTitle') {
        if (!this.employee.positionId && !this.employee.positionTitle) {
          this.errors[field] = 'Position is required';
        } else {
          delete this.errors.positionTitle;
        }
      }

      // Personal Email (mandatory for Contract/Consultant/Intern)
      if (field === 'personalEmail' && this.requiresPersonalEmail) {
        if (!this.employee.personalEmail) {
          this.errors[field] = 'Personal email is required for ' + (this.employee.employmentType || '');
        } else if (!this.isValidEmail(this.employee.personalEmail)) {
          this.errors[field] = 'Please enter a valid personal email address';
        }
      }

      // Force reactivity update
      this.errors = { ...this.errors };
    },

    validateAllFields() {
      // Validate all mandatory fields
      this.validateField('empNo');
      this.validateField('firstName');
      this.validateField('lastName');
      this.validateField('email');
      this.validateField('phone');
      this.validateField('nationalId');
      this.validateField('gender');
      this.validateField('dateOfBirth');
      this.validateField('nationality');
      this.validateField('hireDate');
      this.validateField('departmentID');
      this.validateField('employmentType');
      this.validateField('positionTitle');
      this.validateField('personalEmail');

      return Object.keys(this.errors).length === 0;
    },

    isValidEmail(email) {
      const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return re.test(email);
    },

    isValidPhone(phone) {
      const re = /^[\d\s\-\+\(\)]+$/;
      return re.test(phone) && phone.replace(/\D/g, '').length >= 9;
    },

    async validateAndSubmit() {
      if (!this.validateAllFields()) {
        this.$toast?.error("Please fill in all required fields correctly") ||
        alert("Please fill in all required fields correctly");
        return;
      }

      await this.createEmployee();
    },

    onPositionChange() {
      const pos = this.positionOptions.find(p => p.id == this.employee.positionId);
      if (pos) {
        this.employee.positionTitle = pos.positionName;
        this.employee.position_id = typeof pos.id === 'number' ? pos.id : null;
      } else {
        this.employee.positionTitle = '';
        this.employee.position_id = null;
      }
      this.validateField('positionTitle');
    },

    async createEmployee() {
      this.loading = true;

      try {
        // Get department name
        const selectedDept = this.departments.find(d => d.id === this.employee.departmentID);
        if (selectedDept) {
          this.employee.departmentName = selectedDept.departmentName;
        }
        const pos = this.positionOptions.find(p => p.id == this.employee.positionId);
        if (pos) {
          this.employee.positionTitle = pos.positionName;
          this.employee.position_id = typeof pos.id === 'number' ? pos.id : null;
        } else {
          this.employee.position_id = null;
          if (this.employee.positionId) this.employee.positionTitle = String(this.employee.positionId);
        }

        // Set defaults for optional fields
        const employeeData = {
          ...this.employee,
          middleName: this.employee.middleName || '',
          gender: this.employee.gender || 'Other',
          dateOfBirth: this.employee.dateOfBirth || '1990-01-01',
          nationality: this.employee.nationality || 'Kenya',
          hireDate: this.employee.hireDate || new Date().toISOString().split('T')[0]
        };

        const data = new FormData();
        data.append("function", "createEmployee");
        data.append("employee", JSON.stringify(employeeData));
        data.append("employmentDetails", JSON.stringify({
          department: this.employee.departmentID,
          empNo: employeeData.empNo,
          position_id: this.employee.positionId,
          designation: employeeData.positionTitle || 'Staff',
          employmentDate: employeeData.hireDate,
          reportingDate: '',
          reportingTo: '',
          netSalary: this.employee.netSalary || '0'
        }));
        data.append("extraDetails", JSON.stringify({
          employmentType: employeeData.employmentType,
          educationLevel: this.employee.educationLevel || '',
          subCounty: this.employee.subCounty || '',
          ward: this.employee.ward || '',
          maritalStatus: this.employee.maritalStatus || '',
          drivingLicense: this.employee.drivingLicense || '',
          bloodGroup: this.employee.bloodGroup || '',
          spouseName: this.employee.spouseName || ''
        }));
        data.append("bankDetails", JSON.stringify({
          accountHolder: this.employee.accountHolder || '',
          accountNumber: this.employee.accountNumber || '',
          bankCountry: this.employee.bankCountry || '',
          bankName: this.employee.bankName || '',
          branchLocation: this.employee.branchLocation || '',
          shifNumber: this.employee.shifNumber || '',
          nssfNumber: this.employee.nssfNumber || ''
        }));
        data.append("documents", JSON.stringify([]));
        data.append("nextOfKin", JSON.stringify([]));
        data.append("emergencyContacts", JSON.stringify([]));

        const response = await execute(data, authUrl);

        if (response && response.data && response.data.success) {
          this.successMessage = `${employeeData.firstName} ${employeeData.lastName} has been created successfully. Login details have been emailed (if an email was provided).`;
          this.showSuccessModal = true;
          
          // Auto-navigate to employee list after showing success message
          setTimeout(() => {
            this.showSuccessModal = false;
            this.$router.push({ name: 'employee-register' });
          }, 2000);
        } else {
          const payload = response?.data;
          const errorMsg = this.extractCreateEmployeeError(payload);
          if (payload?.field) {
            this.errors = { ...this.errors, [payload.field]: errorMsg };
          }
          throw new Error(errorMsg);
        }
      } catch (error) {
        console.error('Error creating employee:', error);
        
        let errorMsg = 'An error occurred while creating the employee';
        
        if (error.response?.data) {
          errorMsg = this.extractCreateEmployeeError(error.response.data);
          const field = error.response.data?.field;
          if (field) {
            this.errors = { ...this.errors, [field]: errorMsg };
          }
        } else if (error.message) {
          errorMsg = error.message;
        }
        
        this.errorMessage = errorMsg;
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },

    extractCreateEmployeeError(data) {
      if (!data) {
        return 'Failed to create employee. Please check all required fields.';
      }
      if (typeof data === 'object' && data !== null) {
        return data.message
          || data.error
          || (data.errors ? JSON.stringify(data.errors) : null)
          || 'Failed to create employee. Please check all required fields.';
      }
      if (typeof data === 'string') {
        if (data.includes("Duplicate entry") && data.includes("key 'email'")) {
          return 'An employee with this email address already exists. Please use a different email.';
        }
        if (data.includes('Duplicate entry') && data.includes('nationalId')) {
          return 'An employee with this National ID already exists.';
        }
        if (data.includes('Duplicate entry') && data.includes('empNo')) {
          return 'An employee with this employee number already exists.';
        }
        const plain = data.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        if (plain && !plain.startsWith('Fatal error')) {
          return plain;
        }
      }
      return 'Failed to create employee. Please check all required fields.';
    },

    saveAsDraft() {
      // Store in localStorage for later completion
      const draftData = {
        employee: this.employee,
        savedAt: new Date().toISOString()
      };

      localStorage.setItem('employeeDraft', JSON.stringify(draftData));

      this.$toast?.success("Draft saved successfully!") ||
      alert("Draft saved successfully!");
    },

    loadDraft() {
      const draft = localStorage.getItem('employeeDraft');
      if (draft) {
        const draftData = JSON.parse(draft);
        this.employee = draftData.employee;
        this.showOptionalSections = true;

        this.$toast?.info("Draft loaded") ||
        console.log("Draft loaded");
      }
    },

    resetForm() {
      if (confirm("Are you sure you want to reset the form? All entered data will be lost.")) {
        // Reset all data
        this.employee = {
          empNo: '',
          firstName: '',
          middleName: '',
          lastName: '',
          email: '',
          personalEmail: '',
          phone: '',
          nationalId: '',
          gender: '',
          dateOfBirth: '',
          nationality: 'Kenya',
          employmentType: '',
          hireDate: '',
          departmentID: '',
          departmentName: ''
        };

        this.errors = {};
        this.showOptionalSections = false;
      }
    },

    createAnother() {
      this.resetForm();
      window.scrollTo(0, 0);
    },

    createAnotherAndCloseModal() {
      this.showSuccessModal = false;
      this.createAnother();
    },

    gotTo(route) {
      this.$router.push({ name: route });
    }
  },

  created() {
    // Check for draft on load
    const draft = localStorage.getItem('employeeDraft');
    if (draft) {
      if (confirm("A draft was found. Would you like to load it?")) {
        this.loadDraft();
      }
    }
  }
};
</script>

<style scoped>
/* Clear fixed sidebar (Navigation is 280px wide) */
#layout-wrapper {
  min-height: 100vh;
}
.content-minimize {
  margin-left: 280px;
  padding: 20px;
  transition: margin-left 0.3s ease;
}
@media (max-width: 992px) {
  .content-minimize {
    margin-left: 0;
    padding: 15px;
  }
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #5b73e8;
  box-shadow: 0 0 0 0.2rem rgba(91, 115, 232, 0.25);
}

.is-invalid {
  border-color: #f46a6a !important;
}

.optional-sections {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.badge {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.avatar-lg {
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

/* Loading state */
.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Card hover effects */
.card {
  transition: box-shadow 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Input group styling */
.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
}

.input-group .form-control,
.input-group .form-select {
  border-left: none;
}

.input-group:focus-within .input-group-text {
  border-color: #5b73e8;
  color: #5b73e8;
}

/* Modal overlay (no Bootstrap) */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.modal-dialog-custom {
  max-width: 500px;
  width: 90%;
}
.modal-content-custom {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.modal-body-custom {
  padding: 24px;
}
.d-grid { display: flex; flex-direction: column; gap: 12px; }
.btn-outline-primary { background: transparent; border: 2px solid #5b73e8; color: #5b73e8; }
.btn-outline-primary:hover { background: #5b73e8; color: white; }
</style>